import { api } from "@/features/auth/api";
import type { Order } from "@/features/commerce/api";

declare global {
    interface Window {
        Razorpay?: new (options: Record<string, unknown>) => { open: () => void; on: (e: string, cb: (r: unknown) => void) => void };
    }
}

const SCRIPT_SRC = "https://checkout.razorpay.com/v1/checkout.js";

let checkoutScript: Promise<void> | null = null;

function loadCheckoutScript(): Promise<void> {
    if (window.Razorpay) return Promise.resolve();
    if (checkoutScript) return checkoutScript;
    checkoutScript = new Promise<void>((resolve, reject) => {
        const script = document.createElement("script");
        const fail = () => {
            window.clearTimeout(timer);
            script.remove();
            checkoutScript = null;
            reject(new Error("Razorpay could not load. Check your connection and retry payment."));
        };
        const timer = window.setTimeout(fail, 20000);
        script.src = SCRIPT_SRC;
        script.async = true;
        script.onload = () => {
            window.clearTimeout(timer);
            if (window.Razorpay) resolve();
            else fail();
        };
        script.onerror = fail;
        document.body.appendChild(script);
    });
    return checkoutScript;
}

type CheckoutResult = { razorpay_order_id: string; razorpay_payment_id: string; razorpay_signature: string };

/** Opens Razorpay Checkout for a freshly placed order; resolves with the verified order. */
export async function payWithRazorpay(
    order: Order,
    prefill: { name?: string; email?: string; contact?: string },
): Promise<Order> {
    const p = order.payment;
    if (!p.gateway_order_id || !p.key_id || !p.amount_paise) {
        throw new Error("Online payment could not be started");
    }
    await loadCheckoutScript();
    return new Promise<Order>((resolve, reject) => {
        let finished = false;
        const fail = async (reason: string) => {
            if (finished) return;
            finished = true;
            try {
                await api.post("/payments/razorpay/failed", { order_id: order.id, reason }, { timeout: 10000 });
            } catch {
                // The gateway result, not this advisory request, controls payment status.
            }
            reject(new Error(reason));
        };
        const rz = new window.Razorpay!({
            key: p.key_id,
            amount: p.amount_paise,
            currency: p.currency ?? "INR",
            name: "TSGo",
            description: `Order ${order.number}`,
            order_id: p.gateway_order_id,
            prefill,
            theme: { color: "#16a34a" },
            retry: { enabled: false },
            handler: async (res: CheckoutResult) => {
                if (finished) return;
                finished = true;
                try {
                    const { data } = await api.post<Order>("/payments/razorpay/verify", { order_id: order.id, ...res });
                    if (data.payment.status !== "paid") throw new Error("Payment has not been confirmed yet");
                    resolve(data);
                } catch (err) {
                    reject(err);
                }
            },
            modal: {
                ondismiss: () => { void fail("Payment was not completed. Your order is not confirmed."); },
            },
        });
        rz.on("payment.failed", (r: unknown) => {
            const desc = (r as { error?: { description?: string } })?.error?.description;
            void fail(
                desc
                    ? `Payment was not completed. Your order is not confirmed. (${desc})`
                    : "Payment was not completed. Your order is not confirmed.",
            );
        });
        rz.open();
    });
}
